//#region libraries
import React, { useEffect, useState } from "react";
//bootstrap
import { Container, Button, ButtonGroup } from "react-bootstrap";
//icons
import { FaMobileScreen, FaDesktop, FaClipboardList } from "react-icons/fa6";
//styles
import { Colors } from "_styles";
//custom components
import SurveyRunner from "./survey-runner";
import { CustomSpinner } from "_components/atoms";
import { translate } from "./surveys-utils";
//#endregion

const MOBILE_BREAKPOINT = 768;

// Intro screen mirroring the passenger flow, so the preview shows the full journey.
// Passengers see the generic translated title, never the internal survey name (groupTitle).
// Content is vertically centred with the Start button anchored to the bottom, so the
// screen feels balanced rather than top-heavy.
const IntroScreen = ({ language, onStart }) => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            textAlign: "center",
            direction: language === 'ar' ? 'rtl' : 'ltr',
        }}
    >
        {/* Centred content */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "0 12px" }}>
            <div
                style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: `${Colors.PRM_LIGHT_BLUE}1A`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 20,
                }}
            >
                <FaClipboardList size={28} color={Colors.PRM_LIGHT_BLUE} />
            </div>
            <p style={{ fontSize: 24, fontWeight: 600, color: Colors.PRM_LIGHT_BLUE, marginBottom: 12 }}>
                {translate('title', language)}
            </p>
            <p style={{ fontSize: 16, color: "#555", lineHeight: 1.5, maxWidth: 340, margin: 0 }}>
                {translate('subTitle', language)}
            </p>
        </div>

        {/* Start button anchored to the bottom */}
        <div style={{ paddingTop: 16 }}>
            <Button variant="primary" onClick={onStart} style={{ width: "100%", padding: "12px 0", fontSize: 16, borderRadius: 10 }}>
                {translate('startBtn', language)}
            </Button>
        </div>
    </div>
);

/**
 * Presentational preview wrapper shared by the /survey-preview route (saved survey,
 * fetched) and the creator modal (unsaved draft, flattened client-side). Wraps the
 * real passenger renderer in a device frame with a mobile/desktop toggle and, when
 * more than one language exists, a language switcher. Nothing is ever persisted.
 *
 * @param questions   flattened questions for `language`
 * @param languages   [{ label, value }] available languages (switcher shown when >1)
 * @param language    active language code
 * @param onLanguageChange (value) => void
 * @param loading     show a spinner instead of the survey
 */
const SurveyPreviewView = ({ questions = [], languages = [], language = "en", onLanguageChange, loading = false }) => {

    const [isNarrow, setIsNarrow] = useState(window.innerWidth < MOBILE_BREAKPOINT);
    const [deviceMode, setDeviceMode] = useState("mobile"); // "mobile" | "desktop"
    const [started, setStarted] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsNarrow(window.innerWidth < MOBILE_BREAKPOINT);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Restart the journey when the previewed language changes.
    useEffect(() => { setStarted(false); }, [language]);

    // On a real phone we just fill the screen; on desktop we honour the toggle.
    const framed = !isNarrow && deviceMode === "mobile";

    const frameStyle = framed
        ? {
            // iPhone logical viewport (440 x 956)
            width: 440,
            maxWidth: "100%",
            height: 956,
            maxHeight: "90vh",
            margin: "0 auto",
            border: "12px solid #1f2937",
            borderRadius: 48,
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            overflow: "hidden",
            background: "#fff",
        }
        : {
            width: "100%",
            maxWidth: deviceMode === "desktop" ? 900 : "100%",
            height: isNarrow ? "100%" : "80vh",
            margin: "0 auto",
            border: isNarrow ? "none" : "1px solid #e5e7eb",
            borderRadius: isNarrow ? 0 : 12,
            overflow: "hidden",
            background: "#fff",
        };

    const surveyBody = (
        <Container style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", overflowY: "auto", padding: 16 }}>
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <CustomSpinner size={'small'} />
                </div>
            ) : !started ? (
                <IntroScreen language={language} onStart={() => setStarted(true)} />
            ) : (
                <SurveyRunner
                    key={language}
                    questions={questions}
                    language={language}
                    previewMode
                    renderCompleted={() => (
                        <div style={{ textAlign: "center", padding: 24 }}>
                            <p style={{ fontSize: 22, color: Colors.PRM_LIGHT_BLUE, opacity: 0.8 }}>
                                {translate('finalBtn', language)}
                            </p>
                            <p style={{ fontSize: 16, opacity: 0.7 }}>End of preview — responses are not saved.</p>
                            <Button variant="outline-primary" onClick={() => setStarted(false)}>Restart preview</Button>
                        </div>
                    )}
                />
            )}
        </Container>
    );

    return (
        <div>
            {/* Preview toolbar */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ background: "#FFF7E6", border: "1px solid #FFE0A3", color: "#8a6d1b", borderRadius: 6, padding: "6px 12px", fontSize: 14 }}>
                    Preview — responses are not saved
                </div>

                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    {/* Language switcher (only when multiple languages) */}
                    {languages.length > 1 && (
                        <ButtonGroup size="sm">
                            {languages.map((lang) => (
                                <Button
                                    key={lang.value}
                                    variant={language === lang.value ? "primary" : "outline-primary"}
                                    onClick={() => onLanguageChange?.(lang.value)}
                                >
                                    {lang.label}
                                </Button>
                            ))}
                        </ButtonGroup>
                    )}

                    {/* Device toggle (hidden on real phones) */}
                    {!isNarrow && (
                        <ButtonGroup size="sm">
                            <Button variant={deviceMode === "mobile" ? "primary" : "outline-primary"} onClick={() => setDeviceMode("mobile")} title="Mobile view">
                                <FaMobileScreen />
                            </Button>
                            <Button variant={deviceMode === "desktop" ? "primary" : "outline-primary"} onClick={() => setDeviceMode("desktop")} title="Desktop view">
                                <FaDesktop />
                            </Button>
                        </ButtonGroup>
                    )}
                </div>
            </div>

            <div style={frameStyle}>
                {surveyBody}
            </div>
        </div>
    );
};

export default SurveyPreviewView;
